<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'confirm']);

const store = useStore();
const { t } = useI18n();

// Load loss reasons from store
const storeReasons = computed(
  () => store.getters['lossReasons/getActiveLossReasons'] || []
);

// Contas com dezenas de motivos cadastrados viravam uma lista de radios
// interminável: o combobox com busca resolve sem rolagem.
const lossReasons = computed(() =>
  storeReasons.value.map(reason => ({
    value: String(reason.id),
    label: reason.name,
  }))
);

const hasReasons = computed(() => lossReasons.value.length > 0);

// Fetch loss reasons on mount
onMounted(() => {
  store.dispatch('lossReasons/get', true); // true = active only
});

const selectedReason = ref('');
const notes = ref('');
const isSubmitting = ref(false);

// Watch for modal open
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      // Reset form when modal opens
      selectedReason.value = '';
      notes.value = '';
      isSubmitting.value = false;
    }
  }
);

const contactName = computed(() => {
  return (
    props.conversation?.contact?.name ||
    props.conversation?.meta?.sender?.name ||
    'Cliente'
  );
});

const canSubmit = computed(() => Boolean(selectedReason.value));

const finalReason = computed(() => {
  const reason = lossReasons.value.find(r => r.value === selectedReason.value);
  return reason?.label || selectedReason.value;
});

const handleClose = () => {
  emit('close');
};

const handleConfirm = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;

  try {
    emit('confirm', {
      conversationId: props.conversation.id,
      reason: finalReason.value,
      reasonId: selectedReason.value,
      notes: notes.value,
    });
  } catch (error) {
    // Error handled by parent
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      @click.self="handleClose"
    >
      <div
        class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-5">
          <div class="flex items-center gap-2.5">
            <span
              class="flex h-9 w-9 items-center justify-center rounded-lg bg-n-ruby-3 text-n-ruby-11"
            >
              <span class="i-lucide-circle-x text-lg" />
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('KANBAN.DEAL.LOSE.TITLE') }}
              </h3>
              <p class="truncate text-sm text-n-slate-10">{{ contactName }}</p>
            </div>
          </div>
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="handleClose"
          >
            <span class="i-lucide-x text-base" />
          </button>
        </div>

        <!-- Body -->
        <div class="space-y-4 px-5 pb-2">
          <!-- Info message -->
          <div
            class="flex items-start gap-2.5 rounded-xl border border-n-amber-5 bg-n-amber-3 p-3"
          >
            <span
              class="i-lucide-info mt-0.5 shrink-0 text-base text-n-amber-11"
            />
            <p class="text-sm text-n-amber-12">
              {{ t('KANBAN.DEAL.LOSE.INFO') }}
            </p>
          </div>

          <!-- Loss Reason -->
          <div>
            <label class="mb-2 block text-sm font-medium text-n-slate-11">
              {{ t('KANBAN.DEAL.LOSE.REASON_LABEL') }}
              <span class="text-n-ruby-11">*</span>
            </label>
            <ComboBox
              v-if="hasReasons"
              v-model="selectedReason"
              data-testid="loss-reason-combobox"
              :options="lossReasons"
              :placeholder="t('KANBAN.DEAL.LOSE.REASON_PLACEHOLDER')"
              :search-placeholder="
                t('KANBAN.DEAL.LOSE.REASON_SEARCH_PLACEHOLDER')
              "
              :empty-state="t('KANBAN.DEAL.LOSE.REASON_NOT_FOUND')"
            />
            <p
              v-else
              data-testid="loss-reason-empty"
              class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm text-n-slate-11"
            >
              {{ t('KANBAN.DEAL.LOSE.REASON_NONE_CONFIGURED') }}
            </p>
          </div>

          <!-- Notes (optional) -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              {{ t('KANBAN.DEAL.LOSE.NOTES') }}
              <span class="font-normal text-n-slate-10">
                ({{ t('KANBAN.DEAL.OPTIONAL') }})
              </span>
            </label>
            <textarea
              v-model="notes"
              rows="3"
              :placeholder="t('KANBAN.DEAL.LOSE.NOTES_PLACEHOLDER')"
              class="w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-ruby-8 focus:outline-none focus:ring-2 focus:ring-n-ruby-8/40"
            />
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-2 flex items-center justify-end gap-2 p-5">
          <Button variant="faded" color="slate" @click="handleClose">
            {{ t('KANBAN.DEAL.CANCEL') }}
          </Button>
          <Button
            color="ruby"
            data-testid="lose-confirm"
            :is-loading="isSubmitting"
            :disabled="!canSubmit || isSubmitting"
            @click="handleConfirm"
          >
            <span class="i-lucide-circle-x mr-1" />
            {{ t('KANBAN.DEAL.LOSE.CONFIRM') }}
          </Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
